import type { GlobalInventorySite } from '@/dataAccess/rest/inventory/inventory.types.ts';
import type { SiteConfig, SiteWithApis } from '@/stores';
import { generateApis } from '@/utils/api.ts';

type BuildSitesApisArgs = {
  defaultSite?: SiteWithApis;
  globalInventorySites?: GlobalInventorySite[];
  env?: string;
  portableMode?: boolean;
  forceScaleInternal?: boolean;
};

export const buildSitesApis = ({
  defaultSite,
  globalInventorySites,
  env,
  portableMode,
  forceScaleInternal = false,
}: BuildSitesApisArgs): SiteWithApis[] => {
  const withApis = (site: SiteConfig): SiteWithApis => ({
    ...site,
    apis: generateApis(site, env, portableMode),
  });

  // No-op when the flag is off or the site already targets scale-internal.
  // Otherwise rebuild without the prebuilt `apis` map: getApiUrl short-circuits on a
  // populated apis[api], so spreading ...site would keep the stale non-scale-internal URLs.
  const withForcedScaleInternal = (site: SiteWithApis): SiteWithApis => {
    if (!forceScaleInternal || site.includeScaleInternal) return site;
    return withApis({
      id: site.id,
      name: site.name,
      baseUrl: site.baseUrl,
      includeScaleInternal: true,
    });
  };
  const hasNoInventorySites = !globalInventorySites || globalInventorySites.length === 0;
  if (hasNoInventorySites) {
    return defaultSite ? [withForcedScaleInternal(defaultSite)] : [];
  }

  return globalInventorySites.map(siteInventory => {
    // One edge site keeps the host the app is served on. Its display name often differs from
    // that host. A matching name does the same wherever it occurs, so local dev can keep its host.
    // The id still has to be the inventory id for distinct-types-by-site.
    const sameName =
      defaultSite != null &&
      siteInventory.name.trim().toLowerCase() === defaultSite.name.trim().toLowerCase();
    const keepLocalHost =
      defaultSite != null && ((!portableMode && globalInventorySites.length === 1) || sameName);
    if (keepLocalHost) {
      const site =
        defaultSite.id === siteInventory.id
          ? defaultSite
          : { ...defaultSite, id: siteInventory.id };
      return withForcedScaleInternal(site);
    }

    return withApis({
      id: siteInventory.id,
      name: siteInventory.name,
      baseUrl: siteInventory.fields.SiteBase,
      includeScaleInternal: forceScaleInternal,
    });
  });
};
